import BaseListStore from 'stores/BaseListStore';
import SuppressionRuleStore, { ISuppressionRule } from 'stores/suppressionRule/SuppressionRuleStore';
import { IDataFetchOptions } from '../../types';
import { destroy, get, getPage, post } from 'stores/_services/dataService';
import { getConditions } from 'stores/_services/filterService';
import { makeObservable, action } from 'mobx';

const SEARCH_FIELDS = ['name', 'description'];

export default class SuppressionRuleListStore extends BaseListStore<SuppressionRuleStore> {
    constructor() {
        super({
            itemStore: SuppressionRuleStore
        });

        makeObservable(this, {
            deleteSuppressionRule: action,
            toggleEnabled: action
        });
    }

    async getSuppressionRules(args: IDataFetchOptions = {}): Promise<void> {
        const shouldReset = this._updateListState(args);

        const conditions = getConditions(this.filter);
        conditions.push({ deleted: false });

        const options = {
            filter: {
                searchFields: SEARCH_FIELDS,
                order: this.sort,
                limit: this.limit,
                skip: this.skip,
                where: conditions,
                include: [
                    {
                        relation: 'author',
                        scope: {
                            fields: ['email']
                        }
                    },
                    {
                        relation: 'organization',
                        scope: {
                            fields: ['name', 'data']
                        }
                    },
                    {
                        relation: 'location',
                        scope: {
                            fields: ['name', 'data']
                        }
                    }
                ]
            }
        };

        const { data } = await getPage('suppressionRules', options);

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, this.skip);
        this._updateCount(data);
    }

    async getSuppressionRule(suppressionRuleId: string): Promise<void> {
        const options = {
            filter: {
                includeDeleted: true,
                where: {
                    id: suppressionRuleId
                },
                include: [
                    {
                        relation: 'author',
                        scope: {
                            fields: ['email']
                        }
                    },
                    {
                        relation: 'editor',
                        scope: {
                            fields: ['email']
                        }
                    }
                ]
            }
        };

        const res = await get('suppressionRules', options);

        const {
            data: { name, description, metadata }
        } = await get(`suppressionRules/${suppressionRuleId}/latestVersion`);

        const { data } = res;
        const suppressionRuleData = data[0];

        if (name) {
            suppressionRuleData.name = name;
            suppressionRuleData.description = description;
            suppressionRuleData.metadata = metadata;
        }

        this._updateData(suppressionRuleData);
    }

    async getSuppressionRulePublishDate(): Promise<{ lastPublishDate: string; lastChangeDate: string }> {
        const { data } = await get('settings/getSuppressionRulePublishDate');
        return data;
    }

    async saveSuppressionRule(suppressionRule: ISuppressionRule): Promise<void> {
        const payload = {
            id: suppressionRule.id || undefined,
            alertId: suppressionRule.alertId || undefined,
            description: suppressionRule.description,
            name: suppressionRule.name,
            organizationId: suppressionRule.organizationId,
            locationId: suppressionRule.locationId,
            metadata: JSON.stringify(suppressionRule.metadata || {})
        };

        await post('suppressionRules', payload);
    }

    async toggleEnabled(suppressionRuleId: string): Promise<void> {
        const suppressionRule = this.data.get(suppressionRuleId)?.suppressionRule as ISuppressionRule;

        const payload = {
            id: suppressionRuleId,
            name: suppressionRule.name,
            active: !suppressionRule.active
        };

        await post('suppressionRules/toggleEnabled', payload);
        await this.getSuppressionRule(suppressionRuleId);
    }

    async deleteSuppressionRule(suppressionRuleId: string): Promise<void> {
        await destroy(`suppressionRules/${suppressionRuleId}`);
        await this.getSuppressionRules({ reset: true });
    }

    async publishSuppressionRules(): Promise<void> {
        await post('suppressionRules/publish');
    }
}
